/**
 * Avatar — circular initials chip with a deterministic per-name gradient.
 *
 * Why initials over uploads (v1):
 *   Photos aren't part of the Staff Profile schema yet and AI Members don't
 *   have one to begin with. Deterministic initials give every Member a
 *   recognizable identity without an upload pipeline.
 *
 * Naming & a11y per frontend-conventions.md §10:
 *   - Decorative by default (aria-hidden); pair with a visible name in the
 *     surrounding layout (MemberCard already shows the name).
 *   - Pass `aria-label` when the Avatar stands alone (no adjacent text).
 *   - kind="ai" tints the ring so the AI/human distinction is visible at a
 *     glance — backed by a label, not color alone (see MemberCard kind chip).
 */

import { forwardRef, type HTMLAttributes } from 'react';
import styles from './Avatar.module.css';

type AvatarSize = 'sm' | 'md' | 'lg';
type AvatarKind = 'ai' | 'human';

export interface AvatarProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  name: string;
  size?: AvatarSize;
  kind?: AvatarKind;
}

const SIZE_CLASS: Record<AvatarSize, string | undefined> = {
  sm: styles.sizeSm,
  md: styles.sizeMd,
  lg: styles.sizeLg,
};

const KIND_CLASS: Record<AvatarKind, string | undefined> = {
  ai: styles.kindAi,
  human: styles.kindHuman,
};

// 8 paired stops chosen for distinguishability under both themes. Each pair
// keeps text readable at white over either stop (contrast spot-checked).
const GRADIENT_STOPS: readonly (readonly [string, string])[] = [
  ['#5b6cff', '#8a4dd0'],
  ['#2a7d3f', '#3aa66e'],
  ['#b04848', '#d97a4f'],
  ['#2a5e9c', '#3aa6c8'],
  ['#a66f00', '#d39b3a'],
  ['#5544b3', '#9b6cff'],
  ['#1f5d5d', '#4aa6a6'],
  ['#7a3a8c', '#c46dd0'],
];

function pickGradient(name: string): readonly [string, string] {
  // Cheap, deterministic, no crypto needed — just a string hash mod N.
  let h = 0;
  for (let i = 0; i < name.length; i += 1) {
    h = (h * 31 + name.charCodeAt(i)) >>> 0;
  }
  return GRADIENT_STOPS[h % GRADIENT_STOPS.length]!;
}

function initialsFor(name: string): string {
  const cleaned = name.trim();
  if (!cleaned) return '?';
  // First letters of up to two whitespace-separated tokens.
  const parts = cleaned.split(/\s+/).filter(Boolean).slice(0, 2);
  return parts.map((p) => p[0]!.toUpperCase()).join('');
}

export const Avatar = forwardRef<HTMLSpanElement, AvatarProps>(function Avatar(
  { name, size = 'md', kind = 'human', className, style, ...rest },
  ref,
) {
  const [from, to] = pickGradient(name);
  const composedClassName = [styles.avatar, SIZE_CLASS[size], KIND_CLASS[kind], className ?? '']
    .filter(Boolean)
    .join(' ');

  const ariaLabel = rest['aria-label'];
  const ariaHidden = ariaLabel ? undefined : (rest['aria-hidden'] ?? true);

  return (
    <span
      ref={ref}
      className={composedClassName}
      style={{ backgroundImage: `linear-gradient(135deg, ${from}, ${to})`, ...style }}
      role={ariaLabel ? 'img' : undefined}
      aria-hidden={ariaHidden}
      {...rest}
    >
      <span className={styles.initials}>{initialsFor(name)}</span>
    </span>
  );
});
